import { useSelector } from "react-redux";
import { IAuth } from "../interfaces/IAuth";

/** Reads the `tid` (tenant id) claim from a JWT payload. Display-gating only —
 *  no signature verification (the server enforces access). Returns null on any
 *  malformed token. */
function readTid(token?: string | null): string | null {
  if (!token) return null;
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    const json = JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/")));
    return json?.tid ?? null;
  } catch {
    return null;
  }
}

/** True when the logged-in user is a tenant-admin — i.e. their token carries a
 *  bound tenant (`tid`). Platform super-admins have no `tid`, so this is false. */
export function useIsTenantAdmin(): boolean {
  const accessToken = useSelector((s: { auth: IAuth }) => s.auth.accessToken);
  return readTid(accessToken) !== null;
}

/** The tenant-admin's bound tenant id (`tid`), or null for platform staff.
 *  Used to fetch that tenant's own branding (logo/name) for the header. */
export function useTenantAdminTid(): string | null {
  const accessToken = useSelector((s: { auth: IAuth }) => s.auth.accessToken);
  return readTid(accessToken);
}
